/**
 * One step on the workflow canvas, drawn as AmoregGTM's step card: a 400px
 * card tinted in its kind's accent, the kind's icon and name on top, the
 * step's name and one line about it on a surface below, an inlet dot on top
 * (every step but Start) and an outlet dot at the bottom (every step but
 * Output). Selected, it offers Edit (Open on a run) above its corner.
 *
 * On a run the card says how its step is doing: a Running pill, Needs you
 * while it waits for a person, a check once done, faded when skipped, and a
 * failed step turns red with its reason in a bubble beside it, Retry and
 * Details. While editing, a step with a problem that stops the workflow
 * running shows an amber dot. A step eve or a colleague changed, and this
 * person has not dismissed, wears the change state: its border and tint in
 * the change colour, an Edited or New tag on its corner and its count of
 * changes.
 *
 * @module components/workflows/canvas/node
 */
import type { WorkflowGraphIssue, WorkflowNode, WorkflowStepRun } from "@amoreg/contracts";
import { CheckIcon, InfoIcon, LockIcon, PanelRightOpenIcon, PencilIcon, RotateCcwIcon } from "lucide-react";
import { type CSSProperties, type KeyboardEvent, memo, type PointerEvent as ReactPointerEvent, useEffect, useId, useRef } from "react";

import { cn } from "~/lib/utils";
import { Button } from "../../ui/button";
import { stepStatusLabel, stepDuration, stepTone } from "../workflowCanvas.logic";
import { hasInput, NODE_WIDTH } from "./geometry";
import { GlowText } from "./glowText";
import { DragIcon } from "./icons";
import { KINDS, kindClass } from "./kinds";
import type { CueMotion, StepCue } from "./revisionCues";
import { changeCount, type StepMark } from "../workflowChanges";

export interface CardCue extends StepCue {
  readonly motion: CueMotion;
  readonly descriptionBefore: string | null;
}

const CLOCK = new Intl.DateTimeFormat(undefined, { hour: "2-digit", minute: "2-digit" });
const CHIP =
  "pointer-events-auto inline-block h-5 rounded-full px-1.5 text-[11px] leading-5 font-medium transition-opacity duration-400 ease-power3-out motion-reduce:transition-none starting:opacity-0";

const HANDLE =
  "absolute left-1/2 size-2 -translate-x-1/2 rounded-full bg-kind shadow-[0_0_0_2px_color-mix(in_srgb,var(--kind)_30%,transparent),var(--handle-depth)]";

export interface CanvasStepProps {
  readonly node: WorkflowNode;
  /** One line about the step (its instructions, its sources, who it emails). */
  readonly description: string;
  readonly selected: boolean;
  /** Selected with other steps: no Edit button of its own. */
  readonly grouped?: boolean | undefined;
  /** A link being drawn would end here. */
  readonly targeted: boolean;
  readonly dragging: boolean;
  /** Its panel is open. */
  readonly editing: boolean;
  /** Its hover card is open, which says why it failed: no failure bubble beside it then. */
  readonly hoverOpen?: boolean | undefined;
  readonly editable: boolean;
  /** Shared to run only: the chart is read, its cards locked. */
  readonly locked: boolean;
  readonly issues: ReadonlyArray<WorkflowGraphIssue>;
  /** Its state on the run shown; null while editing. */
  readonly step: WorkflowStepRun | null;
  readonly now: number;
  /** Start's pill: when the workflow runs. */
  readonly startPill?: string | undefined;
  readonly onRetry?: ((nodeId: string) => void) | undefined;
  readonly retrying?: boolean | undefined;
  readonly onMeasure: (id: string, height: number) => void;
  readonly onNodePointerDown: (id: string, event: ReactPointerEvent) => void;
  readonly onHandlePointerDown: (id: string, event: ReactPointerEvent) => void;
  readonly onOpen: (id: string) => void;
  readonly onKeyNav: (id: string, event: KeyboardEvent) => void;
  readonly onFocusNode: (id: string) => void;
  /** The pointer came onto the step or left it (its hover card). */
  readonly onHover?: ((id: string, entering: boolean, pointerType: string) => void) | undefined;
  readonly cue?: CardCue | undefined;
  readonly mark?: StepMark | undefined;
}

function RunPill({ step }: { readonly step: WorkflowStepRun }) {
  const tone = stepTone(step.status);
  if (tone === "running") {
    return (
      <span className="mr-1.5 flex h-5 animate-canvas-fade-in items-center gap-1.5 rounded-full bg-kind/12 px-2 text-[12px] leading-5 font-medium text-kind">
        <span className="size-1.5 animate-pulse rounded-full bg-kind" />
        Running
      </span>
    );
  }
  if (tone === "waiting") {
    return (
      <span className="mr-1.5 flex h-5 items-center gap-1.5 rounded-full bg-(--tone-amber)/12 px-2 text-[12px] leading-5 font-medium text-(--tone-amber)">
        <span className="size-1.5 rounded-full bg-(--tone-amber)" />
        Needs you
      </span>
    );
  }
  if (tone === "done") return <CheckIcon aria-label="Done" className="mr-1.5 size-4 text-(--tone-green)" />;
  if (tone === "failed") {
    return (
      <span className="mr-1.5 flex h-5 items-center gap-1.5 rounded-full bg-(--tone-red)/12 px-2 text-[12px] leading-5 font-medium text-(--tone-red)">
        Failed
      </span>
    );
  }
  return null;
}

function FailureBubble({
  nodeId,
  message,
  onRetry,
  onDetails,
  retrying,
}: {
  readonly nodeId: string;
  readonly message: string;
  readonly onRetry?: ((nodeId: string) => void) | undefined;
  readonly onDetails: (nodeId: string) => void;
  readonly retrying: boolean;
}) {
  return (
    <div
      role="alert"
      data-workflow-failure={nodeId}
      onPointerDown={(event) => event.stopPropagation()}
      className="absolute top-0 left-full z-10 ml-4 w-72 animate-canvas-rise-in cursor-default rounded-lg bg-canvas-popover p-3.5 text-left shadow-canvas-popover backdrop-blur-[12px]"
    >
      <span aria-hidden className="absolute top-5 -left-1.5 size-3 rotate-45 bg-canvas-popover shadow-[-1px_1px_0_var(--canvas-line)]" />
      <p className="text-[12px] leading-5 font-medium text-(--tone-red)">This step failed</p>
      <p className="mt-1 text-[13px] leading-5 text-foreground">{message}</p>
      <div className="mt-3 flex items-center gap-1.5">
        {onRetry ? (
          <Button size="xs" variant="destructive" disabled={retrying} onClick={() => onRetry(nodeId)}>
            <RotateCcwIcon aria-hidden />
            {retrying ? "Retrying…" : "Retry this step"}
          </Button>
        ) : null}
        <Button size="xs" variant="ghost" onClick={() => onDetails(nodeId)}>
          Details
        </Button>
      </div>
    </div>
  );
}

function CanvasStepView({
  node,
  description,
  selected,
  grouped = false,
  targeted,
  dragging,
  editing,
  hoverOpen = false,
  editable,
  locked,
  issues,
  step,
  now,
  startPill,
  onRetry,
  retrying = false,
  onMeasure,
  onNodePointerDown,
  onHandlePointerDown,
  onOpen,
  onKeyNav,
  onFocusNode,
  onHover,
  cue,
  mark,
}: CanvasStepProps) {
  const ref = useRef<HTMLDivElement>(null);
  const meta = KINDS[node.type];
  const Icon = meta.icon;
  const tone = stepTone(step?.status ?? null);
  const duration = step ? stepDuration(step, now) : null;

  useEffect(() => {
    const element = ref.current;
    if (!element || typeof ResizeObserver === "undefined") return;
    // A card taken off the page reports no height: its last one stays, for the ghost left where it stood.
    const observer = new ResizeObserver(() => {
      if (element.isConnected) onMeasure(node.id, element.offsetHeight);
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, [node.id, onMeasure]);

  const cueColor = cue?.glow?.color;
  const style = {
    "--node-x": `${node.position.x}px`,
    "--node-y": `${node.position.y}px`,
    ...(cueColor ? { "--cue": cueColor } : {}),
    width: NODE_WIDTH,
  } as CSSProperties;
  const label = `${meta.label}: ${node.label}`;
  const notes = useId();

  const corner = step ? (
    <RunPill step={step} />
  ) : issues.length > 0 ? (
    <span
      role="img"
      aria-label={issues.map((issue) => issue.message).join(" ")}
      title={issues.map((issue) => issue.message).join(" ")}
      className="mr-1.5 size-2 rounded-full bg-(--tone-amber)"
      data-workflow-step-issue=""
    />
  ) : editable && node.type !== "start" ? (
    <span className="flex items-center justify-end pr-0.5 opacity-0 transition-opacity duration-150 ease-power3-in-out group-hover/node:opacity-100">
      <DragIcon className="size-4 text-muted-foreground" />
    </span>
  ) : locked && node.type !== "start" ? (
    <span className="flex items-center justify-end pr-1 opacity-0 transition-opacity duration-150 ease-power3-in-out group-hover/node:opacity-100">
      <LockIcon aria-hidden className="size-3.5 text-muted-foreground" />
    </span>
  ) : null;

  return (
    <div
      ref={ref}
      data-node={node.id}
      data-workflow-step={node.id}
      data-workflow-step-tone={tone}
      data-selected={selected || undefined}
      data-targeted={targeted || undefined}
      data-dragging={dragging || undefined}
      data-tone={tone}
      data-workflow-change-mark={mark?.kind}
      style={style}
      className={cn(
        kindClass(node.type),
        "group/node absolute top-0 left-0 [translate:var(--node-x)_var(--node-y)] transition-opacity duration-200 data-dragging:z-10",
        tone === "skipped" && "opacity-45",
        cue?.moving && "transition-[translate,opacity] duration-400 ease-power3-out",
        cue?.entering && "transition-[opacity,scale] duration-200 ease-power3-out starting:scale-[0.96] starting:opacity-0",
      )}
    >
      <div
        role="button"
        tabIndex={0}
        aria-label={label}
        aria-pressed={selected}
        onPointerDown={(event) => onNodePointerDown(node.id, event)}
        onPointerEnter={(event) => onHover?.(node.id, true, event.pointerType)}
        onPointerLeave={(event) => onHover?.(node.id, false, event.pointerType)}
        onDoubleClick={() => onOpen(node.id)}
        // Right-click opens the step's settings, like a double-click; no browser menu.
        onContextMenu={(event) => {
          event.preventDefault();
          event.stopPropagation();
          onOpen(node.id);
        }}
        onFocus={() => onFocusNode(node.id)}
        onKeyDown={(event) => onKeyNav(node.id, event)}
        className={cn(
          "relative flex flex-col gap-[3px] rounded-lg border border-kind/20 bg-kind/10 px-[5px] pt-[5px] pb-[7px] outline-none backdrop-blur-[16px] transition-[border-color,box-shadow] duration-150 ease-power3-in-out",
          editable ? "cursor-grab group-data-dragging/node:cursor-grabbing" : "cursor-pointer",
          "group-data-dragging/node:shadow-canvas-drag",
          // Selected: the full accent border, a tinted card and a glow in the step's colour.
          "group-data-selected/node:border-kind group-data-selected/node:bg-kind/15 group-data-selected/node:shadow-[0_0_0_3px_color-mix(in_srgb,var(--kind)_30%,transparent),0_10px_28px_-10px_color-mix(in_srgb,var(--kind)_60%,transparent)]",
          "group-data-targeted/node:border-kind/70 group-data-targeted/node:shadow-[0_0_0_4px_color-mix(in_srgb,var(--kind)_16%,transparent)]",
          tone === "running" && "border-kind/80",
          tone === "waiting" && "border-(--tone-amber)/70",
          tone === "failed" && "border-(--tone-red)/70 shadow-[0_0_0_4px_color-mix(in_srgb,var(--tone-red)_14%,transparent)]",
          tone === "skipped" && "border-dashed",
          "focus-visible:shadow-[0_0_0_2px_var(--canvas-panel),0_0_0_4px_color-mix(in_srgb,var(--ring)_60%,transparent)]",
          mark && "border-(--workflow-change) bg-(--workflow-change)/8",
        )}
      >
        {mark ? (
          <span
            className="absolute -top-[9px] right-3 rounded-sm bg-[color-mix(in_srgb,var(--workflow-change)_16%,var(--canvas-panel))] px-1.5 text-[11px] leading-4 font-medium text-(--workflow-change) shadow-[0_0_0_1px_color-mix(in_srgb,var(--workflow-change)_45%,transparent)]"
            data-workflow-change-tag=""
          >
            {mark.kind === "added" ? "New" : "Edited"}
          </span>
        ) : null}
        <div className="flex h-9 items-center px-1 py-1.5">
          <span className="flex shrink-0 items-center pl-1">
            <Icon aria-hidden className="size-[18px] text-kind" />
          </span>
          <span className="flex min-w-0 flex-1 items-center gap-2 pr-4 pl-2">
            <span className="truncate text-[14px] leading-6 font-medium text-kind">{meta.label}</span>
          </span>
          {corner}
          {node.type === "start" && startPill ? (
            <span className="relative flex h-5 items-center overflow-clip rounded-[21px] bg-kind/15 bg-[linear-gradient(176.7deg,var(--pill-sheen))] px-[9px] text-[12px] leading-6 font-semibold text-kind uppercase shadow-[inset_0_1px_0_var(--pill-highlight)]">
              {startPill}
            </span>
          ) : null}
        </div>
        <div className="flex flex-col gap-1 rounded-lg bg-canvas-surface px-3.5 py-3 text-[14px]">
          <GlowText
            text={node.label}
            before={cue?.glow?.before.label}
            glowKey={cue?.glow?.key}
            motion={cue?.motion ?? "animate"}
            className="truncate leading-5 font-semibold text-foreground"
          />
          {description ? (
            <GlowText
              text={description}
              before={cue?.descriptionBefore ?? undefined}
              glowKey={cue?.glow?.key}
              motion={cue?.motion ?? "animate"}
              className="line-clamp-2 leading-5 font-medium text-faint"
            />
          ) : null}
          {mark && !step ? (
            <span
              className="mt-1.5 flex items-center gap-1.5 pt-2 text-[12px] leading-4 font-medium text-(--workflow-change) shadow-[0_-1px_0_color-mix(in_srgb,var(--foreground)_6%,transparent)]"
              data-workflow-change-count=""
            >
              <InfoIcon aria-hidden className="size-3.5" />
              {changeCount(mark.changes)}
            </span>
          ) : null}
          {step && step.status !== "pending" ? (
            <span
              className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 pt-2 text-[12px] leading-4 text-muted-foreground shadow-[0_-1px_0_color-mix(in_srgb,var(--foreground)_6%,transparent)]"
              data-workflow-step-status={step.status}
            >
              <span className="font-medium text-foreground">{stepStatusLabel(step.status, node.type)}</span>
              {duration ? (
                <>
                  <span aria-hidden className="size-[3px] rounded-full bg-foreground/30" />
                  <span className="tabular-nums">{duration}</span>
                </>
              ) : null}
              {step.attempt > 1 ? (
                <>
                  <span aria-hidden className="size-[3px] rounded-full bg-foreground/30" />
                  <span>attempt {step.attempt}</span>
                </>
              ) : null}
            </span>
          ) : null}
        </div>
      </div>

      {hasInput(node.type) ? <span aria-hidden className={cn(HANDLE, "-top-1")} /> : null}
      {node.type !== "output" ? (
        <span
          role="presentation"
          data-workflow-handle={node.id}
          onPointerDown={(event) => onHandlePointerDown(node.id, event)}
          className={cn(HANDLE, "-bottom-1", editable && "cursor-crosshair before:absolute before:-inset-2 before:rounded-full")}
        />
      ) : null}

      <div onPointerDown={(event) => onNodePointerDown(node.id, event)} className="pointer-events-none absolute -top-7 left-0 flex max-w-full items-center gap-1.5">
        <span
          role="note"
          aria-label={cue?.chip?.title}
          aria-describedby={`${notes}-chip`}
          title={cue?.chip?.title}
          data-workflow-cue-chip={cue?.chip?.label}
          style={cue?.chip ? ({ "--chip": cue.chip.color } as CSSProperties) : undefined}
          className={cn(
            CHIP,
            "max-w-60 truncate bg-[color-mix(in_srgb,var(--chip)_16%,transparent)] text-[color-mix(in_srgb,var(--chip)_75%,var(--foreground))] before:content-[attr(data-workflow-cue-chip)]",
            !cue?.chip && "hidden",
          )}
        />
        <span
          role="note"
          aria-describedby={`${notes}-kept`}
          title={cue?.kept ?? undefined}
          data-workflow-cue-kept={cue?.kept ? "" : undefined}
          className={cn(CHIP, "bg-foreground/8 whitespace-nowrap text-muted-foreground", !cue?.kept && "hidden")}
        >
          Kept your edit
        </span>
        <span hidden id={`${notes}-chip`}>
          {cue?.chip ? `Changed at ${CLOCK.format(cue.chip.at)}` : "Changed"}
        </span>
        <span hidden id={`${notes}-kept`}>
          {cue?.kept ?? "Kept your edit"}
        </span>
      </div>

      {selected && !grouped && !dragging && !editing ? (
        <div
          onPointerDown={(event) => event.stopPropagation()}
          className="absolute -top-10 right-0 transition-[opacity,translate] duration-150 ease-power3-out starting:translate-y-1 starting:opacity-0"
        >
          <Button size="sm" onClick={() => onOpen(node.id)} className="shadow-canvas-popover" data-workflow-step-open={node.id}>
            {editable ? <PencilIcon /> : <PanelRightOpenIcon />}
            {editable ? "Edit step" : "Open"}
          </Button>
        </div>
      ) : null}

      {step?.status === "failed" && !hoverOpen ? (
        <FailureBubble
          nodeId={node.id}
          message={step.error?.message ?? "This step failed."}
          onRetry={onRetry}
          onDetails={onOpen}
          retrying={retrying}
        />
      ) : null}
    </div>
  );
}

export const CanvasStep = memo(CanvasStepView);
